/**
 * The library as a signed-out visitor sees it.
 *
 * public_techniques needs auth (firestore.rules), so the free preview can't use
 * useTechniques' Firestore listener. This reads the same index from a public
 * endpoint instead. The response deliberately carries no source URLs — playback
 * resolves one clip at a time through previewVideoUrl below.
 */
import type { Technique } from '../types';
import { markPreviewExhausted } from '../previewGate';
import { requestAccount } from '../guestMode';

const INDEX_API = import.meta.env.VITE_LIBRARY_INDEX_URL || '/api/library-index';
const PREVIEW_VIDEO_API = import.meta.env.VITE_PREVIEW_VIDEO_URL || '/api/preview-video';

/**
 * Stands in for the Instagram URL a signed-in user's copy of a Technique
 * carries. Everything downstream keys playability off `instagramUrl` being
 * truthy, and the feed pool drops anything without one — so guests get an
 * opaque handle that only previewVideoUrl knows how to resolve.
 */
export const PREVIEW_URL_SCHEME = 'gnosis-preview:';

export function previewUrlFor(id: string) {
  return `${PREVIEW_URL_SCHEME}${id}`;
}

export function techniqueIdFromPreviewUrl(url: string): string | null {
  return url.startsWith(PREVIEW_URL_SCHEME) ? url.slice(PREVIEW_URL_SCHEME.length) : null;
}

interface IndexRow {
  id: string;
  name: string;
  japaneseName: string | null;
  aliases: string[] | null;
  categoryId: string | null;
  tier: string | null;
  type: string | null;
  uniform: string | null;
  arts: string[] | null;
  thumbnail: string | null;
  author: string | null;
  attribution: string | null;
  context: string | null;
  rule: string | null;
  createdAt: number | null;
}

/** Mirrors mapDoc in hooks/useTechniques.ts, minus the fields guests don't get. */
function toTechnique(r: IndexRow): Technique {
  return {
    id: r.id,
    name: r.name,
    japaneseName: r.japaneseName || undefined,
    aliases: r.aliases?.length ? r.aliases : undefined,
    categoryId: r.categoryId as Technique['categoryId'],
    tier: r.tier as unknown as Technique['tier'],
    type: r.type as unknown as Technique['type'],
    uniform: (r.uniform as unknown as Technique['uniform']) || undefined,
    arts: (r.arts as unknown as Technique['arts']) || undefined,
    instagramUrl: previewUrlFor(r.id),
    thumbnail: r.thumbnail || undefined,
    author: r.author || undefined,
    attribution: r.attribution || undefined,
    context: r.context || undefined,
    rule: r.rule || undefined,
    createdAt: r.createdAt ?? undefined,
  };
}

/** The index, fetched once per page load. Several rails ask for it, and they
 *  mount and unmount as the reader moves between Home's tabs — without this
 *  every switch is another round trip for a list that doesn't change. A
 *  failure isn't kept, so the next mount tries again. */
let libraryIndex: Promise<Technique[]> | null = null;

export function fetchPreviewLibrary(): Promise<Technique[]> {
  if (!libraryIndex) {
    libraryIndex = (async () => {
      const resp = await fetch(INDEX_API);
      if (!resp.ok) throw new Error(`library index ${resp.status}`);
      const body = (await resp.json()) as { techniques?: IndexRow[] };
      return (body.techniques ?? []).map(toTechnique);
    })().catch((err) => {
      libraryIndex = null;
      throw err;
    });
  }
  return libraryIndex;
}

/**
 * The playable URL for one clip. Null when the source post no longer resolves.
 *
 * 402 is the server refusing on the free allowance — the clip is fine and the
 * request was well formed, they're just out. Raise the wall rather than let it
 * surface as "This link is broken", and bring the local counter into line so
 * the UI stops offering plays the server will refuse.
 */
export async function previewVideoUrl(techniqueId: string): Promise<string | null> {
  const resp = await fetch(`${PREVIEW_VIDEO_API}?id=${encodeURIComponent(techniqueId)}`, {
    // Same-origin in production (nginx proxies it), but be explicit: the
    // allowance rides an httpOnly cookie and is meaningless without it.
    credentials: 'same-origin',
  });
  if (resp.status === 402) {
    markPreviewExhausted();
    requestAccount('plays');
    throw new Error('preview-limit');
  }
  if (!resp.ok) throw new Error(`preview video ${resp.status}`);
  const body = (await resp.json()) as { videoUrl?: string | null };
  return body.videoUrl ?? null;
}

/** The precomputed docs the Home rails read, for a signed-out visitor. */
export interface HomeStats {
  promotions: Record<string, unknown> | null;
  mostStarred: Record<string, unknown> | null;
  upcomingEvents: Record<string, unknown> | null;
  /** Sport legality. Without it a guest sees banned techniques unhidden and
   *  unlabelled — see useRules in utils/legality.ts. */
  rules: unknown[] | null;
}

const HOME_STATS_API = import.meta.env.VITE_HOME_STATS_URL || '/api/home-stats';

export async function fetchHomeStats(): Promise<HomeStats> {
  const resp = await fetch(HOME_STATS_API);
  if (!resp.ok) throw new Error(`home stats ${resp.status}`);
  return (await resp.json()) as HomeStats;
}
